import { isNativeApp } from './platform.js';
import { saveOfflinePdf } from './offlinePapers.js';

const accessCache = new Map();
const accessInflight = new Map();
const pdfBufferCache = new Map();
const pdfInflight = new Map();

export const OPEN_DAILY_PAPER_EVENT = 'rk-open-daily-paper';
export const OPEN_MONTHLY_PAPER_EVENT = 'rk-open-monthly-paper';

function accessKey(kind, id, preview) {
  return `${kind}:${id}:${preview ? 'preview' : 'full'}`;
}

/** PDF.js may transfer/detach ArrayBuffers — always work from an independent copy. */
export function clonePdfBytes(buf) {
  if (!buf) return null;
  if (buf instanceof ArrayBuffer) return buf.slice(0);
  if (ArrayBuffer.isView(buf)) {
    return buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength);
  }
  return buf;
}

export function clearPaperCache() {
  accessCache.clear();
  accessInflight.clear();
  pdfBufferCache.clear();
  pdfInflight.clear();
}

export function clearDailyPaperAccess(dateKey, preview = false) {
  const key = accessKey('daily', dateKey, preview);
  const cached = accessCache.get(key);
  accessCache.delete(key);
  accessInflight.delete(key);
  if (cached?.pdfUrl) {
    pdfBufferCache.delete(cached.pdfUrl);
    pdfInflight.delete(cached.pdfUrl);
  }
}

export function clearMonthlyPaperAccess(monthKey) {
  const key = accessKey('monthly', monthKey, false);
  const cached = accessCache.get(key);
  accessCache.delete(key);
  accessInflight.delete(key);
  if (cached?.pdfUrl) {
    pdfBufferCache.delete(cached.pdfUrl);
    pdfInflight.delete(cached.pdfUrl);
  }
}

export async function ensureDailyPaperAccess(api, authToken, dateKey, { preview = false, force = false } = {}) {
  const key = accessKey('daily', dateKey, preview);
  if (force) clearDailyPaperAccess(dateKey, preview);

  const cached = accessCache.get(key);
  if (cached) return cached;

  let inflight = accessInflight.get(key);
  if (!inflight) {
    inflight = (async () => {
      const q = preview ? '?preview=1' : '';
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const res = await fetch(`${api}/api/papers/${dateKey}/view${q}`, { headers });
      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        const err = new Error(data.error || 'Login required');
        err.code = 'LOGIN';
        err.status = 401;
        throw err;
      }
      if (res.status === 403) {
        const err = new Error(data.error || 'Subscription required');
        err.code = 'SUBSCRIBE';
        err.status = 403;
        throw err;
      }
      if (!res.ok || !data.token) {
        const err = new Error(data.error || 'Paper not available');
        err.status = res.status;
        throw err;
      }

      const result = {
        pdfUrl: `${api}/papers/${dateKey}/file?token=${data.token}`,
        maxPages: data.maxPages ?? null,
        preview: !!data.preview,
        dateKey,
        kind: 'daily',
      };
      accessCache.set(key, result);
      prefetchPdfBytes(result.pdfUrl);
      return result;
    })().finally(() => {
      accessInflight.delete(key);
    });
    accessInflight.set(key, inflight);
  }

  return inflight;
}

export async function ensureMonthlyPaperAccess(api, authToken, monthKey, { force = false } = {}) {
  const key = accessKey('monthly', monthKey, false);
  if (force) clearMonthlyPaperAccess(monthKey);

  const cached = accessCache.get(key);
  if (cached) return cached;

  let inflight = accessInflight.get(key);
  if (!inflight) {
    inflight = (async () => {
      const res = await fetch(`${api}/api/monthly-papers/${monthKey}/view`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 403) {
        const err = new Error(data.error || 'Subscription required');
        err.code = 'SUBSCRIBE';
        err.status = 403;
        throw err;
      }
      if (!res.ok || !data.token) {
        const err = new Error(data.error || 'Paper not available');
        err.status = res.status;
        throw err;
      }

      const result = {
        pdfUrl: `${api}/monthly-papers/${monthKey}/file?token=${data.token}`,
        maxPages: null,
        preview: false,
        monthKey,
        kind: 'monthly',
      };
      accessCache.set(key, result);
      prefetchPdfBytes(result.pdfUrl);
      return result;
    })().finally(() => {
      accessInflight.delete(key);
    });
    accessInflight.set(key, inflight);
  }

  return inflight;
}

export function prefetchPdfBytes(pdfUrl) {
  if (!pdfUrl || pdfBufferCache.has(pdfUrl) || pdfInflight.has(pdfUrl)) return;
  const task = fetch(pdfUrl, { credentials: 'include' })
    .then((res) => {
      if (!res.ok) throw new Error(`PDF prefetch failed: ${res.status}`);
      return res.arrayBuffer();
    })
    .then((buf) => {
      // Keep an owned copy in cache; callers must clone again before handing to PDF.js.
      pdfBufferCache.set(pdfUrl, clonePdfBytes(buf));
      pdfInflight.delete(pdfUrl);
      return pdfBufferCache.get(pdfUrl);
    })
    .catch(() => {
      pdfInflight.delete(pdfUrl);
    });
  pdfInflight.set(pdfUrl, task);
}

export function getCachedPdfBuffer(pdfUrl) {
  const cached = pdfBufferCache.get(pdfUrl);
  if (!cached) return null;
  // Always return a fresh copy so PDF.js cannot detach the cached buffer.
  try {
    return clonePdfBytes(cached);
  } catch {
    pdfBufferCache.delete(pdfUrl);
    return null;
  }
}

export function getPdfBytes(pdfUrl) {
  const cached = getCachedPdfBuffer(pdfUrl);
  if (cached) return Promise.resolve(cached);
  if (pdfInflight.has(pdfUrl)) {
    return pdfInflight.get(pdfUrl).then((buf) => clonePdfBytes(buf));
  }
  prefetchPdfBytes(pdfUrl);
  const task = pdfInflight.get(pdfUrl);
  if (!task) return Promise.reject(new Error('Prefetch failed'));
  return task.then((buf) => {
    if (!buf) throw new Error('Prefetch failed');
    return clonePdfBytes(buf);
  });
}

/** Put bytes into the in-memory cache under an arbitrary key (e.g. blob: URL). */
export function putPdfBuffer(pdfUrl, buffer) {
  if (!pdfUrl || !buffer) return;
  pdfBufferCache.set(pdfUrl, clonePdfBytes(buffer));
}

export function getCachedDailyAccess(dateKey, preview = false) {
  return accessCache.get(accessKey('daily', dateKey, preview)) ?? null;
}

export function getCachedMonthlyAccess(monthKey) {
  return accessCache.get(accessKey('monthly', monthKey, false)) ?? null;
}

export function warmDailyPaper(api, authToken, dateKey, { preview = false } = {}) {
  if (!api || !dateKey) return;
  if (!authToken && !preview) return;
  ensureDailyPaperAccess(api, authToken, dateKey, { preview })
    .then(async (access) => {
      const bytes = await getPdfBytes(access.pdfUrl);
      if (!preview && isNativeApp()) void saveOfflinePdf('daily', dateKey, bytes);
    })
    .catch(() => {});
}

export function warmMonthlyPaper(api, authToken, monthKey) {
  if (!api || !authToken || !monthKey) return;
  ensureMonthlyPaperAccess(api, authToken, monthKey)
    .then(async (access) => {
      const bytes = await getPdfBytes(access.pdfUrl);
      if (isNativeApp()) void saveOfflinePdf('monthly', monthKey, bytes);
    })
    .catch(() => {});
}
